import { useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Box } from '@mui/material';
import BlockProps from '../../../models/BlockProps';
import BlockSNSOldData from './BlockSNSOldData';
import ImageSettingsModal from '../../Modals/ImageSettingsModal/ImageSettingsModal';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import EditableText from '../../Form/EditableText';
import { generateIconItemId } from './BlockSNSOldPreview';
import useResponsive from '../../../hooks/UseResponsive';
import { useGetBase64Image } from '../../../hooks/UseGetBase64Image';
import ImageModal from '../../Modals/ImageModal/ImageModal';
import CroppedImage from '../../CroppedImage';
import config from '../../../config';
import BlockData from '../../../models/BlockData';
import ModalComponent from '../../ModalComponent';
import BlockAlignPicker from '../../Modals/BlockSettingsModal/components/BlockAlignPicker';
import { BlockAlign } from '../../../models/BlockSettings';

export interface BlockSNSOldProps extends BlockProps {
  blockItems?: BlockData[]
  showAlignmentOptionsModal?: boolean
  onCloseAlignmentModal?: () => void
}

const BlockSNSOldEdit = (props: BlockSNSOldProps) => {
  const {
    data, editHandler, isTemplate, isEditing, showImageModal, showAlignmentOptionsModal, onCloseImageModal, onCloseAlignmentModal, blockItems = [],
  } = props;

  const [imageSettingShow, setImageSettingShow] = useState(false);

  const blockId = data.id;
  const blockData = data.data as BlockSNSOldData;
  const { icons } = blockData;
  const [alignmentTemp, setAlignmentTemp] = useState(blockData.iconsAlignment ?? BlockAlign.START);

  const iconsItemIds = icons.map((icon, key) => generateIconItemId(data.id, key));

  useResponsive({
    items: iconsItemIds.map((iconsItemId) => ({
      wrapperId: `sns-icon-block-container-${data.id}`,
      targetId: iconsItemId,
      queries: [
        { size: 0, className: 'col-4' },
        { size: 500, className: 'col-sm-2' },
      ],
    })),
  });

  const handleTitleDeleted = useCallback(() => {
    delete blockData.title;
    editHandler(blockId, blockData);
  }, [blockId, blockData, editHandler]);

  const handleLeftPicDeleted = useCallback(() => {
    delete blockData.leftPic;
    editHandler(blockId, blockData);
  }, [blockId, blockData, editHandler]);

  const handleRightSubTitleDeleted = useCallback(() => {
    delete blockData.rightSubTitle;
    editHandler(blockId, blockData);
  }, [blockId, blockData, editHandler]);

  const handleRightSubTitle2Deleted = useCallback(() => {
    delete blockData.rightSubTitle2;
    editHandler(blockId, blockData);
  }, [blockId, blockData, editHandler]);

  const handleDescriptionDeleted = useCallback(() => {
    delete blockData.description;
    editHandler(blockId, blockData);
  }, [blockId, blockData, editHandler]);

  const handleIconImageDeleted = useCallback((id: string) => {
    const target = blockData.icons.find((icon) => icon.id === id);
    if (target == null) {
      return;
    }

    // タイトルをすでに削除された場合は、さらにアイコン画像を削除したいなら、全体を削除します。
    if (target.title == null) {
      blockData.icons = blockData.icons.filter((icon) => icon.id !== id);
      editHandler(blockId, blockData);
      return;
    }
    delete target.iconUrl;
    editHandler(blockId, blockData);
  }, [blockId, blockData, editHandler]);

  const handleIconTextDeleted = useCallback((id: string) => {
    const target = blockData.icons.find((icon) => icon.id === id);
    if (target == null) {
      return;
    }

    // アイコンはすでに削除された場合は、さらにアイコンテキストを削除したいなら、全体を削除します。
    if (target.iconUrl == null) {
      blockData.icons = blockData.icons.filter((icon) => icon.id !== id);
      editHandler(blockId, blockData);
      return;
    }

    delete target.title;
    editHandler(blockId, blockData);
  }, [blockId, blockData, editHandler]);

  const handleIconAdded = useCallback(() => {
    blockData.icons.push({ title: 'Text', iconUrl: config.defaultImageUrl, id: uuid() });
    editHandler(blockId, blockData);
  }, [blockId, blockData, editHandler]);

  const handleSaveAlignment = useCallback(() => {
    blockData.iconsAlignment = alignmentTemp;
    editHandler(blockId, blockData);
    onCloseAlignmentModal();
  }, [blockData, alignmentTemp, blockId, editHandler, onCloseAlignmentModal]);

  const base64Image = useGetBase64Image(blockData.url?.value);

  return (
    <>
      <div
        className="section-block"
        id="section-1001"
        style={{
          overflow: 'hidden',
          backgroundImage: `url(${base64Image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {!isTemplate && (
          <ImageModal
            showTitle={false}
            showModalFromOutside={showImageModal}
            onCloseModalFromOutside={onCloseImageModal}
            handleOnConfirm={(url: string) => {
              if (!blockData.url) {
                blockData.url = new BlockComponentImage();
              }
              blockData.url.value = url;
              editHandler(data.id, blockData);
            }}
          />
        )}
        <div className="p-wrapper p-wrapper-sm">
          <div className="p-inner">
            {blockData.title && (
              <div className="row">
                <div className="col-xs-12">
                  <div className="p-parts-group mb-4">
                    <div className="p-parts-item">
                      <div
                        className="edit--item"
                        data-bs-toggle="modal"
                      >
                        <div className="ql-align-center">
                          <EditableText
                            text={blockData.title.value}
                            textColor={blockData.title.settings?.color}
                            isEditable={isEditing && !isTemplate}
                            updateHandler={(text: string) => {
                              blockData.title.value = text;
                              editHandler(data.id, blockData);
                            }}
                            textClassName="sns-block-title-edit"
                          />
                        </div>
                      </div>
                      <div className="p1-toolbox p1-toolbox-objdelete fade show" id="">
                        <button onClick={handleTitleDeleted} className="btn btn-parts-delete" type="button">
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              {blockData.leftPic && (
                <div className="col-xs-12 col-sm-6">
                  <div className="p-parts-group mb-4">
                    <div className="p-parts-item">
                      <div
                        className="edit--item"
                        data-bs-toggle="modal"
                        data-bs-target="#imageEditModal"
                        id="section-1001-002-001"
                      >
                        <div
                          className="p-img"
                          onClick={() => {
                            if (isTemplate || !isEditing) {
                              return;
                            }
                            setImageSettingShow(true);
                          }}
                        >
                          <CroppedImage image={blockData.leftPic} settings={blockData.leftPic?.settings} />
                        </div>
                        <div className="p1-toolbox p1-toolbox-objdelete fade show" id="">
                          <button onClick={handleLeftPicDeleted} className="btn btn-parts-delete" type="button">
                            <i className="fa-solid fa-xmark" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <ImageSettingsModal
                    image={blockData.leftPic}
                    isEditing={isEditing}
                    showModal={imageSettingShow}
                    disabled={isTemplate || !isEditing}
                    blockData={blockItems}
                    onConfirm={(newImage: BlockComponentImage) => {
                      blockData.leftPic = newImage;
                      editHandler(data.id, blockData);
                      setImageSettingShow(false);
                    }}
                    closeModal={() => {
                      setImageSettingShow(false);
                    }}
                  />
                </div>
              )}

              <div className="col-xs-12 col-sm-6">
                <div className="p-parts-group mb-4">
                  {blockData.rightSubTitle && (
                    <div className="p-parts-item">
                      <div className="edit--item">
                        <EditableText
                          text={blockData.rightSubTitle.value}
                          textColor={blockData.rightSubTitle.settings?.color}
                          isEditable={isEditing && !isTemplate}
                          updateHandler={(text: string) => {
                            blockData.rightSubTitle.value = text;
                            editHandler(data.id, blockData);
                          }}
                          textClassName="sns-block-right-sub-title-edit"
                        />
                      </div>
                      <div className="p1-toolbox p1-toolbox-objdelete fade show" id="">
                        <button onClick={handleRightSubTitleDeleted} className="btn btn-parts-delete" type="button">
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    </div>
                  )}

                  {blockData.rightSubTitle2 && (
                    <div className="p-parts-item mt-3">
                      <div className="edit--item">
                        <EditableText
                          text={blockData.rightSubTitle2.value}
                          textColor={blockData.rightSubTitle2.settings?.color}
                          isEditable={isEditing && !isTemplate}
                          updateHandler={(text: string) => {
                            blockData.rightSubTitle2.value = text;
                            editHandler(data.id, blockData);
                          }}
                          textClassName="sns-block-right-sub-title2-edit"
                        />
                      </div>
                      <div className="p1-toolbox p1-toolbox-objdelete fade show" id="">
                        <button
                          onClick={handleRightSubTitle2Deleted}
                          className="btn btn-parts-delete"
                          type="button"
                        >
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    </div>
                  )}
                  {blockData.description && (
                    <div className="p-parts-item mt-3">
                      <div className="edit--item">
                        <EditableText
                          text={blockData.description.value}
                          textColor={blockData.description.settings?.color}
                          isEditable={isEditing && !isTemplate}
                          updateHandler={(text: string) => {
                            blockData.description.value = text;
                            editHandler(data.id, blockData);
                          }}
                          textClassName=""
                        />
                      </div>
                      <div className="p1-toolbox p1-toolbox-objdelete fade show" id="">
                        <button onClick={handleDescriptionDeleted} className="btn btn-parts-delete" type="button">
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div id={`sns-icon-block-container-${data.id}`} className={`row align-items-stretch row-gap-4 justify-content-${blockData.iconsAlignment}`}>
              {icons && icons.map((icon, key) => (
                <div id={generateIconItemId(data.id, key)} key={generateIconItemId(data.id, key)}>
                  <div className="p-parts">
                    {icon.iconUrl && (
                      <div className="p-parts-item">
                        <div
                          className="edit--item"
                          data-bs-toggle="modal"
                          data-bs-target="#imageEditModal"
                          id="section-1001-003-001"
                        >
                          <div className="p-img">
                            <ImageSettingsModal
                              isEditing={isEditing}
                              image={BlockComponentImage.make(icon.iconUrl, undefined, undefined, icon.link)}
                              disabled={isTemplate || !isEditing}
                              blockData={blockItems}
                              onConfirm={(newImage: BlockComponentImage) => {
                                blockData.icons[key].pic = newImage;
                                blockData.icons[key].link = newImage.link;
                                editHandler(data.id, blockData);
                                setImageSettingShow(false);
                              }}
                            >
                              <CroppedImage image={icon.pic || BlockComponentImage.make(icon.iconUrl)} settings={icon.pic?.settings || BlockComponentImage.make(icon.iconUrl).settings} />
                            </ImageSettingsModal>
                          </div>
                        </div>
                        <div className="p1-toolbox p1-toolbox-objdelete fade show" id="">
                          <button onClick={() => handleIconImageDeleted(icon.id)} className="btn btn-parts-delete" type="button">
                            <i className="fa-solid fa-xmark" />
                          </button>
                        </div>
                      </div>
                    )}
                    {icon.title && (
                      <div className="p-parts-item">
                        <div
                          className="edit--item"
                          data-bs-toggle="modal"
                          data-bs-target="#textEditorModal"
                          id="section-1001-003-002"
                        >
                          <div className="ql-align-center">
                            <EditableText
                              text={icon.title}
                              textColor={icon?.titleColor}
                              isEditable={isEditing && !isTemplate}
                              updateHandler={(text: string) => {
                                blockData.icons[key].title = text;
                                editHandler(data.id, blockData);
                              }}
                              textClassName="sns-block-icon-text"
                            />
                          </div>
                        </div>
                        <div className="p1-toolbox p1-toolbox-objdelete fade show" id="">
                          <button onClick={() => handleIconTextDeleted(icon.id)} className="btn btn-parts-delete" type="button">
                            <i className="fa-solid fa-xmark" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <Box className="col-4 col-sm-2" sx={{ minHeight: '113px' }}>
                <div className="p-parts-group h-100">
                  <div className="p-parts-item h-100" onClick={handleIconAdded}>
                    <div className="edit--item h-100 d-flex justify-content-center align-items-center">
                      <i className="fa-solid fa-plus fa-fw" />
                      Add
                    </div>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      </div>

      {showAlignmentOptionsModal && (
        <ModalComponent
          show={showAlignmentOptionsModal}
          confirmButtonText="Save"
          closeModal={() => { onCloseAlignmentModal(); }}
          onConfirm={handleSaveAlignment}
        >
          <BlockAlignPicker
            value={alignmentTemp}
            direction="column"
            hasStretch={false}
            onChangeAlign={
              (value: BlockAlign) => {
                setAlignmentTemp(value);
              }
            }
          />
        </ModalComponent>
      )}
    </>
  );
};

export default BlockSNSOldEdit;
